import type { ReactNode } from "react";
import { SlideHeader } from "../../../SlideHeader";

interface LogoEntry {
  src: string;
  alt: string;
}

const LOGO_HEIGHT_CLASS = "h-5 md:h-7";

const YT_LOGO_ROWS: ReadonlyArray<ReadonlyArray<LogoEntry>> = [
  [
    { src: "https://public.apptonomy.ai/funplus.svg", alt: "FunPlus" },
    { src: "https://public.apptonomy.ai/g5.svg", alt: "G5 Games" },
    { src: "https://public.apptonomy.ai/kilohealth.svg", alt: "Kilo.Health" },
  ],
  [
    { src: "https://public.apptonomy.ai/tiltingpoint.svg", alt: "Tilting Point" },
    { src: "https://public.apptonomy.ai/fabulous.svg", alt: "Fabulous" },
    { src: "https://public.apptonomy.ai/mindvalley.svg", alt: "Mindvalley" },
  ],
];

const TPF_LOGO_ROWS: ReadonlyArray<ReadonlyArray<LogoEntry>> = [
  [
    { src: "https://public.apptonomy.ai/netflix.svg", alt: "Netflix" },
    { src: "https://public.apptonomy.ai/google.svg", alt: "Google" },
    { src: "https://public.apptonomy.ai/facebook.svg", alt: "Facebook" },
  ],
  [
    { src: "https://public.apptonomy.ai/messagebird.svg", alt: "MessageBird" },
    { src: "https://public.apptonomy.ai/welltech.svg", alt: "Welltech" },
  ],
];

export default function FoundersWhyUs() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:px-24 md:pt-16 md:pb-8">
      <SlideHeader eyebrow="Why Us">
        <h1 className="text-3xl font-semibold leading-tight tracking-tight md:text-6xl md:leading-[1.1]">
          Designed by an ASO veteran.
          <br />
          Built by a product veteran.
        </h1>
      </SlideHeader>

      <div className="grid grid-cols-1 gap-10 md:flex-1 md:grid-cols-2 md:gap-16">
        <Founder
          name="Yevhen Tarasenko"
          role="Co-founder & CMO"
          location="Barcelona"
          linkedinHandle="yevhentarasenko"
          avatarSrc="https://public.apptonomy.ai/yt-profile-pic.png"
          logoRows={YT_LOGO_ROWS}
        >
          <span className="font-semibold text-slate-900">
            12+ years of hands-on ASO
          </span>{" "}
          at FunPlus, KiloHealth, Tilting Point, Fabulous, Mindvalley, and
          others. Established ASO/Organic Growth departments across most of
          those companies. Consistently grew organic installs and revenue
          across gaming, health &amp; fitness, and lifestyle.
        </Founder>
        <Founder
          name="Thomas Purnell-Fisher"
          role="Co-founder & CEO"
          location="Amsterdam"
          linkedinHandle="purnellfisher"
          avatarSrc="https://public.apptonomy.ai/tpf-bnw-256x.png"
          logoRows={TPF_LOGO_ROWS}
        >
          <span className="font-semibold text-slate-900">
            20+ years building products at scale.
          </span>{" "}
          First PM on Netflix streaming. Helped YouTube launch on TV at Google.
          Built and led cross-functional teams of ~100. Interim CPO through
          pivots, raises, and acquisitions including MessageBird's $800M Series
          C and Welltech. Co-inventor on four patents.
        </Founder>
      </div>

      <p className="mt-8 border-t border-slate-200 pt-5 text-center text-lg font-semibold italic tracking-tight text-slate-700 md:mt-6 md:pt-6 md:text-2xl">
        The methodology author and the platform builder —{" "}
        <span className="text-blue-700">
          Apptonomy is the practitioner, encoded.
        </span>
      </p>
      <p className="mt-2 text-center text-sm font-medium leading-snug text-slate-600 md:mt-2.5 md:text-lg">
        We are the company's first investors — the build to date is
        founder-funded, and we both work on it full-time.
      </p>
    </div>
  );
}

interface FounderProps {
  name: string;
  role: string;
  location: string;
  linkedinHandle: string;
  avatarSrc: string;
  children: ReactNode;
  logoRows?: ReadonlyArray<ReadonlyArray<LogoEntry>>;
}

function Founder({
  name,
  role,
  location,
  linkedinHandle,
  avatarSrc,
  children,
  logoRows,
}: FounderProps) {
  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-4 md:gap-6">
        <img
          src={avatarSrc}
          alt={name}
          className="h-16 w-16 rounded-full object-cover ring-1 ring-slate-200 md:h-24 md:w-24"
        />
        <div>
          <p className="text-xl font-semibold tracking-tight md:text-3xl">
            {name}
          </p>
          <p className="mt-1 text-sm text-slate-600 md:text-lg">{role}</p>
          <p className="mt-1.5 flex items-center gap-2 text-xs text-slate-500 md:mt-2 md:text-base">
            <span>{location}</span>
            <span aria-hidden="true">·</span>
            <a
              href={`https://www.linkedin.com/in/${linkedinHandle}`}
              target="_blank"
              rel="noreferrer"
              aria-label={`${name} on LinkedIn`}
              className="inline-flex transition hover:opacity-70"
            >
              <img
                src="https://public.apptonomy.ai/linkedin.svg"
                alt=""
                className="h-4 w-4"
              />
            </a>
          </p>
        </div>
      </div>
      <p className="mt-5 text-base leading-relaxed text-slate-700 md:mt-7 md:text-xl">
        {children}
      </p>
      {logoRows && logoRows.length > 0 ? (
        <div className="mt-6 flex flex-col gap-3 md:mt-auto md:gap-4 md:pt-8">
          {logoRows.map((row, rowIndex) => (
            <div
              key={rowIndex}
              className="flex flex-wrap items-center gap-x-3 gap-y-3 md:gap-x-6"
            >
              {row.map((logo) => (
                <img
                  key={logo.alt}
                  src={logo.src}
                  alt={logo.alt}
                  className={`${LOGO_HEIGHT_CLASS} w-auto opacity-90`}
                />
              ))}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
